import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { NavigationStart, Router, RouterModule } from '@angular/router';
import { MatTabsModule } from '@angular/material/tabs';
import { COLORS } from '@constants/colors';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { TAB } from '@constants/constants';
import { OverlayService } from '@services/ui/overlay.service';
import { TPipe } from '@pipes/t.pipe';
import { Subscription } from 'rxjs';

@Component({
  selector: 'abb-tabs',
  standalone: true,
  template: `
    <nav mat-tab-nav-bar [tabPanel]="tabPanel">
      @for (tab of tabs(); track tab) {
      <a
        mat-tab-link
        [id]="tab['link']"
        class="!text-xs !h-10 !font-semibold !flex-none uppercase"
        [active]="activeTab() == tab['link']"
        [routerLink]="tab['link']"
      >
        {{ tab['label'] | t }}
      </a>
      } @if(canEdit()){
      <span class="flex-auto flex justify-end pr-4">
        @if(showBtn()){
        <abb-button class="w-36" id="action-btn" (click)="openSidePanel()">{{
          btnLabel() | t
        }}</abb-button>
        }
      </span>
      }
    </nav>
    <mat-tab-nav-panel
      class="flex flex-col flex-1 w-full overflow-auto"
      #tabPanel
    >
      <router-outlet></router-outlet>
    </mat-tab-nav-panel>
  `,
  styles: `
      .mat-mdc-tab-group, .mat-mdc-tab-nav-bar {
        --mat-tab-header-disabled-ripple-color: rgb(var(--base-value-color-font) / 0.38);
        --mat-tab-header-pagination-icon-color: var(--abb-color-font);
        --mat-tab-header-inactive-label-text-color: rgb(var(--base-value-color-font) / 0.6);
        --mat-tab-header-inactive-focus-label-text-color: rgb(var(--base-value-color-font) / 0.6);
        --mat-tab-header-inactive-hover-label-text-color: rgb(var(--base-value-color-font) / 0.6);
        --mdc-tab-indicator-active-indicator-height: 6px;
      }
      ::ng-deep router-outlet ~ * {
          height: 100%;
      }
  `,
  imports: [MatTabsModule, RouterModule, AbbButtonComponent, TPipe],
})
export class AbbTabsComponent implements OnInit, OnDestroy {
  readonly COLORS: COLORS = COLORS;
  canEdit = input<boolean>();
  tabs = input<Array<TAB>>([]);
  tabChange = output<string>();
  activeTab = signal('');
  btn = computed(
    () => this.tabs().find((t) => t.link == this.activeTab())?.btn
  );
  showBtn = computed(() => this.btn() != undefined);
  btnLabel = computed(() => this.btn()?.label);
  sidePanel = computed(() => this.btn()?.sidePanel);

  private subscriptions: Subscription[] = [];
  constructor(
    private readonly router: Router,
    private readonly overlayService: OverlayService
  ) {
    this.subscriptions.push(
      router.events.subscribe((e) => {
        if (e instanceof NavigationStart) {
          const decodedUrl = decodeURIComponent(e.url);
          this.activeTab.set(
            this.tabs().find((t) => decodedUrl.includes(t.link))?.link ??
              this.tabs()[0]['link']
          );
          this.tabChange.emit(this.activeTab());
        }
      })
    );
  }

  ngOnInit(): void {
    const decodedUrl = decodeURIComponent(this.router.url);
    this.activeTab.set(
      this.tabs().find((t) => decodedUrl.includes(t['link']))?.['link'] ??
        this.tabs()[0]['link']
    );
    this.tabChange.emit(this.activeTab());

    if (!decodedUrl.includes(this.activeTab())) {
      this.router.navigate([`${decodedUrl}/${this.activeTab()}`]);
    }
  }

  openSidePanel(): void {
    this.overlayService.openSidePanel(
      this.sidePanel()!.component,
      this.sidePanel()?.data,
      this.sidePanel()?.options
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
